import dayjs from 'dayjs'
import { sortBy } from 'lodash-es'
import { DictionaryItem } from '../../bewotec/openapi'
import { DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT, STAGE, URLs, globalHelper } from '../../general/helper'
import { IUrls } from '../../general/interfaces'
import { CLASSICAI, COMFORTAI, INDAI, PAUSCHAL, PAUSCHALAI } from '../../general/master-data'
import defaultValues, { DefaultValues } from './defaultValues'
import {
  FieldValue,
  FilterFieldDropDownType,
  Holiday,
  HolidayRegion,
  HolidaysByCountry,
  IFilterFields,
  IFilterPresets,
  IParsedFilterPresets,
  ResponseComment,
  ResponseHoliday
} from './interface'

export const parseFilterPreset = (queryStrings: string) => {
  const paramString = queryStrings.substring(1)
  const modified = paramString.replace(/=/g, '":"').replace(/&/g, '","')
  const parsableString = `{"${modified}"}`

  const rawPresets: IFilterPresets = JSON.parse(parsableString)
  const mappedPresets: IParsedFilterPresets = {}

  for (const [key, value] of Object.entries(rawPresets)) {
    // this is needed otherwise Prod is breaking the filter fields
    // since there are also other URL params, which are not filters
    // e.g. agencyId, utm_source,...
    if (!!defaultValues[key as keyof IFilterFields]) {
      const mappedValue = turnStringsToNumbers(key, value)
      let fieldValue: FieldValue = mappedValue

      switch (key) {
        case 'campaigns':
          fieldValue = mapCampaigns(mappedValue)
          break
        case 'departureDate':
          const departureDate = convertDate(mappedValue)
          const isPastDate = dayjs(departureDate, DEFAULT_DATE_FORMAT).isBefore(dayjs())

          fieldValue = isPastDate ? null : departureDate
          break
        case 'arrivalDate':
          const arrivalDate = convertDate(mappedValue)
          const comparedDate = rawPresets.departureDate

          fieldValue = mapArrivalDate(comparedDate, arrivalDate)
          break
        default:
          if (typeof mappedValue === 'string' && defaultValues[key as keyof IFilterFields]?.type === 'flags') {
            fieldValue = mappedValue.split(',')
          }
          break
      }

      mappedPresets[key] = fieldValue
    }
  }

  return mappedPresets
}

const turnStringsToNumbers = (key: string, value: string) => {
  const defaultKeys = Object.keys(defaultValues)
  const numberKeys = defaultKeys.filter(
    defaultKey => typeof defaultValues[defaultKey as keyof DefaultValues].value === 'number'
  )

  return numberKeys.includes(key) ? parseInt(value, 10) : value
}

const mapCampaigns = (value: string | number) => {
  const filterItems = !!value ? value.toString().split(',') : []
  const mappedFilterItems = []

  for (const urlItem of filterItems) {
    const replacedPlus = urlItem.replaceAll('+', ' ')
    mappedFilterItems.push(replacedPlus.replaceAll('%20', ' '))
  }

  return mappedFilterItems
}

const convertDate = (value: string | number) => {
  value = value.toString()

  if (value.includes('-')) {
    return dayjs(value, ISO_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
  }

  return value
}

const mapArrivalDate = (departureDate: string, date: string) => {
  const arrivalDate = dayjs(date, DEFAULT_DATE_FORMAT)

  if (
    (!!departureDate && !arrivalDate.isAfter(dayjs(departureDate, DEFAULT_DATE_FORMAT))) ||
    !arrivalDate.isAfter(dayjs())
  ) {
    return null
  }
  return date
}

export const getModifiedQueryStrings = (fieldName: keyof IFilterFields, fieldValue: FieldValue) => {
  const innerSection = new RegExp(fieldName + '=.*?&')
  const lastSection = new RegExp(`${fieldName}=.*`)

  if (!location.search) {
    return `?${fieldName}=${fieldValue}`
  }
  if (!location.search.includes(fieldName)) {
    return `${location.search}&${fieldName}=${fieldValue}`
  }
  if (location.search.match(innerSection)) {
    return location.search.replace(innerSection, `${fieldName}=${fieldValue}&`)
  }

  return location.search.replace(lastSection, `${fieldName}=${fieldValue}`)
}

export const updateQueryStrings = (fieldName: keyof IFilterFields, fieldValue: FieldValue) => {
  const defaultValue = defaultValues[fieldName].value
  const isDefaultSelection =
    Array.isArray(fieldValue) && Array.isArray(defaultValue) && fieldValue?.length === defaultValue?.length

  if (!fieldValue || fieldValue === defaultValue || isDefaultSelection) {
    filterHelper.removeFromQueryStrings(fieldName)
    return
  }

  const queryStrings = filterHelper.getModifiedQueryStrings(fieldName, fieldValue)
  const replacedPlus = queryStrings.replaceAll(' ', '+')

  const path = URLs[STAGE as keyof IUrls].expinet + replacedPlus.replaceAll(' ', '%20')
  globalHelper.changeDisplayedURL(path)
}

export const removeFromQueryStrings = (fieldName: keyof IFilterFields) => {
  let queryStrings = location.search
  const posFieldName = queryStrings.indexOf(`${fieldName}=`)

  if (posFieldName < 0) {
    return
  }

  const posNextAmp = queryStrings.indexOf('&', posFieldName)
  const headLength = posNextAmp < 0 ? posFieldName - 1 : posFieldName
  const head = queryStrings.substring(0, headLength)
  const tail = posNextAmp < 0 ? '' : queryStrings.substring(posNextAmp + 1)
  queryStrings = head + tail
  const path = URLs[STAGE as keyof IUrls].expinet + queryStrings

  globalHelper.changeDisplayedURL(path)
}

export const addExtraKeyWords = (type: FilterFieldDropDownType, value: DictionaryItem) => {
  let extraKeyWords: string[] = []

  if (type === 'cruiseAttributes') {
    switch (value.code) {
      case 'CustomProperties.Custom1':
        extraKeyWords = ['VRSE']
        break
      case 'CustomProperties.Custom2':
        extraKeyWords = ['VRSP']
        break
    }
  }

  if (type === 'airports') {
    extraKeyWords = [`${value.name} (${value.code})`]
  }

  if (type === 'priceModels') {
    switch (value.code) {
      case INDAI:
        extraKeyWords = ['PREMIUMAI']
        break
      case CLASSICAI:
        extraKeyWords = ['CLASSICAI']
        break
      case COMFORTAI:
        extraKeyWords = ['COMFORTAI']
        break
      case PAUSCHALAI:
        extraKeyWords = ['PAUSCHALAI']
        break
      case PAUSCHAL:
        extraKeyWords = ['PAUSCHAL']
        break
    }
  }

  if (type === 'categories') {
    switch (value.code) {
      case 'B':
        extraKeyWords = ['Balkon']
        break
      case 'V':
        extraKeyWords = ['Veranda']
        break
    }
  }

  return extraKeyWords
}

export const sortValuesBySortOrder = (values: DictionaryItem[], sortOrder: string[]) => {
  const sortedValues = []

  for (const order of sortOrder) {
    for (const value of values) {
      if (value.code === order) {
        sortedValues.push(value)
        break
      }
    }
  }

  return sortedValues
}

export const sortValuesAlphabetically = (values: DictionaryItem[]) => {
  let sortedValues: DictionaryItem[] = []

  if (values.length) {
    sortedValues = sortBy(values, ['name'])
  }
  return sortedValues
}

export const mapHolidays = (holidays: ResponseHoliday[], holidaysByCountry: HolidaysByCountry): Holiday[] => {
  const mappedHolidays: Holiday[] = []

  for (const holiday of holidays) {
    // exclude berufsbildende Schulen
    if (holiday.groups?.length === 1 && !!holiday.groups.find(group => group.code.includes('BBS'))) {
      continue
    }

    const holidayName = holiday.name.find(n => n.language === 'DE')?.text ?? holiday.name[0].text

    const mappedHoliday: Holiday = {
      startDate: holiday.startDate,
      endDate: holiday.endDate,
      name: `${holidayName}`,
      nationwide: holiday.nationwide,
    }

    if (!!holiday.subdivisions?.length) {
      for (const subdivision of holiday.subdivisions) {
        const regionCodes = subdivision.code.split('-')
        const facilities = getFacilities(holiday.comment)

        if (!mappedHoliday.regions) {
          mappedHoliday.regions = []
        }

        const newRegion: HolidayRegion = {
          code: regionCodes[1],
          name: `${holidaysByCountry[regionCodes[0]].federalStates[regionCodes[1]].stateName}`,
        }

        if (!!facilities.length) {
          newRegion.includedFacilities = facilities
        }

        mappedHoliday.regions?.push(newRegion)
      }
    }

    mappedHolidays.push(mappedHoliday)
  }

  const sortedAndMappedHolidays = mappedHolidays.map(holiday => {
    holiday.regions?.sort((a, b) => a.name.localeCompare(b.name))
    return holiday
  })

  return sortedAndMappedHolidays
}

export const sortHolidays = (holidays: Holiday[]) => {
  return holidays.sort((a, b) => {
    return (a.regions?.[0]?.name || '').localeCompare(b.regions?.[0]?.name || '')
  })
}

const getFacilities = (comments: ResponseComment[] | undefined): string[] => {
  const facilities: string[] = []

  if (!!comments?.length) {
    comments.map(comment => {
      facilities.push(comment.text)
    })
  }

  return facilities.sort((a, b) => a.localeCompare(b))
}

export const filterHelper = {
  getModifiedQueryStrings,
  updateQueryStrings,
  removeFromQueryStrings,
}

// -- For Testing Purposes Only --------------------------------

export const exportForTesting = {
  getFacilities,
  turnStringsToNumbers,
  mapArrivalDate,
  convertDate,
  mapCampaigns,
}
